import React, {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  Dispatch
} from 'react'
import { TestMode } from 'types/enum/enum'
import { HintsTypes } from 'types/types/hintsTypes'
import { ChatItem, Question, Quiz } from 'types/types/testData'
import { DECREMENT_LIMIT, INCREMENT_LIMIT } from 'variables/constants'

// Define the State interface

interface State {
  isExplanationOpen: boolean
  testMode: TestMode
  testData: Quiz | null
  filteredTestData?: Quiz | null
  testExplanationData: Question[] | null
  currentPage: number
  isModalOpen: boolean
  formType:
    | 'mark'
    | 'submit'
    | 'resume'
    | 'quick-test'
    | 'quick-result'
    | 'mobile-settings'
    | 'unlock'
    | null
  chat: ChatItem[] | null
  isChatOpen: boolean
  testTime: number
  questionsTimers: number[]
  isAutoSwipe?: boolean
  isNotification?: boolean
  testHints?: HintsTypes
  testFilter?: 'mistake' | 'correct' | null
  textSizeIncrement: number
  isReportOpen: boolean
}

interface TestReportAction {
  type: 'TOGGLE_REPORT'
}
interface TextSizeIncrementAction {
  type: 'TEXT_SIZE_INCREMENT'
  payload: number
}

interface TextSizeDecrementAction {
  type: 'TEXT_SIZE_DECREMENT'
  payload: number
}

interface DefaultTextSizeAction {
  type: 'DEFAULT_TEXT_SIZE'
}

interface QuestionTimersAction {
  type: 'SET_QUESTION_TIMERS'
  payload: number[]
}

// Extend the Action interface to include the new action type and payload
interface ToggleExplanationAction {
  type: 'TOGGLE_EXPLANATION'
}
interface toggleAutoSwipeAction {
  type: 'TOGGLE_AUTO_SWIPE'
}
interface toggleNotificationAction {
  type: 'TOGGLE_NOTIFICATION'
}

interface TestTimeAction {
  type: 'SET_TEST_TIME'
  payload: number
}

interface ToggleChatAction {
  type: 'TOGGLE_CHAT'
}

interface ToggleModalAction {
  type: 'TOGGLE_MODAL'
  payload:
    | 'mark'
    | 'submit'
    | 'resume'
    | 'quick-result'
    | 'mobile-settings'
    | 'unlock'
    | null
}

interface SetChatDataAction {
  type: 'SET_CHAT_DATA'
  payload: ChatItem[]
}

interface SetTestDataAction {
  type: 'SET_TEST_DATA'
  payload: Quiz
}
interface SetFilteredTestDataAction {
  type: 'SET_FILTERED_TEST_DATA'
  payload: Quiz | null
}
interface SetExplanationDataAction {
  type: 'SET_TEST_EXPLANATION_DATA'
  payload: Question[]
}

interface ChangetestTypeAction {
  type: 'CHANGE_TEST_MODE'
  payload: TestMode
}

interface SetCurrentPageAction {
  type: 'SET_CURRENT_PAGE'
  payload: number
}

interface NextPageAction {
  type: 'NEXT_PAGE'
}

interface PrevPageAction {
  type: 'PREV_PAGE'
}

interface TestHintsAction {
  type: 'TOGGLE_HINTS'
  payload: 'hint-and-key-info' | 'key-info' | 'hint' | null
}
interface TestFilterAction {
  type: 'TOGGLE_FILTER'
  payload: 'mistake' | 'correct' | null
}

type Action =
  | ToggleExplanationAction
  | ChangetestTypeAction
  | SetTestDataAction
  | SetCurrentPageAction
  | NextPageAction
  | PrevPageAction
  | ToggleModalAction
  | SetChatDataAction
  | ToggleChatAction
  | TestTimeAction
  | QuestionTimersAction
  | SetExplanationDataAction
  | toggleAutoSwipeAction
  | toggleNotificationAction
  | TestHintsAction
  | TestFilterAction
  | SetFilteredTestDataAction
  | TextSizeIncrementAction
  | TextSizeDecrementAction
  | DefaultTextSizeAction
  | TestReportAction

// Define ContextProps interface
interface ContextProps {
  state: State
  dispatch: Dispatch<Action>
}

// Define the initial state
const initialState: State = {
  isExplanationOpen: false,
  testMode: TestMode.TUTOR,
  testData: null,
  filteredTestData: null,
  testExplanationData: null,
  currentPage: 0, // Initialize currentPage in initialState
  isModalOpen: false,
  formType: null,
  chat: null,
  isChatOpen: false,
  testTime: 0,
  questionsTimers: [],
  isAutoSwipe: false,
  isNotification: false,
  testHints: null,
  testFilter: null,
  textSizeIncrement: 0,
  isReportOpen: false
}

// Create the context
const TestContext = createContext<ContextProps | undefined>(undefined)

// Define the reducer function with explicit type guards
function TestReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_QUESTION_TIMERS': {
      return { ...state, questionsTimers: action.payload }
    }
    case 'SET_TEST_TIME': {
      return { ...state, testTime: action.payload }
    }
    case 'TOGGLE_CHAT': {
      return { ...state, isChatOpen: !state.isChatOpen }
    }
    case 'SET_CHAT_DATA': {
      return { ...state, chat: action.payload }
    }
    case 'TOGGLE_EXPLANATION': {
      return {
        ...state,
        isExplanationOpen: !state.isExplanationOpen
      }
    }
    case 'CHANGE_TEST_MODE': {
      let updatedTestData = state.testData

      // Check if the test data exists and if the mode has not been changed before
      if (updatedTestData && !updatedTestData.metadata?.is_mode_changed) {
        // Set the is_mode_changed flag
        updatedTestData = {
          ...updatedTestData,
          metadata: {
            ...updatedTestData.metadata,
            is_mode_changed: true
          }
        }
      }

      // Return the updated state with the new test mode and updated test data
      return {
        ...state,
        testMode: action.payload,
        testData: updatedTestData
      }
    }
    case 'TOGGLE_MODAL': {
      return {
        ...state,
        isModalOpen: !state.isModalOpen,
        formType: action.payload
      }
    }
    case 'SET_TEST_DATA': {
      return { ...state, testData: action.payload } // Reset currentPage when setting test data
    }
    case 'SET_FILTERED_TEST_DATA': {
      return { ...state, filteredTestData: action.payload }
    }
    case 'SET_TEST_EXPLANATION_DATA': {
      return { ...state, testExplanationData: action.payload } // Reset currentPage when setting test data
    }
    case 'SET_CURRENT_PAGE': {
      return { ...state, currentPage: action.payload }
    }
    case 'NEXT_PAGE': {
      const currentTestData = state.filteredTestData || state.testData
      if (currentTestData) {
        const nextPage =
          state.currentPage === currentTestData.quiz.length - 1
            ? 0
            : state.currentPage + 1
        return {
          ...state,
          currentPage: nextPage
        }
      }
      return state
    }
    case 'PREV_PAGE': {
      const currentTestData = state.filteredTestData || state.testData
      if (currentTestData) {
        const prevPage =
          state.currentPage === 0
            ? currentTestData.quiz.length - 1
            : state.currentPage - 1
        return {
          ...state,
          currentPage: prevPage
        }
      }
      return state
    }
    case 'TOGGLE_AUTO_SWIPE': {
      return { ...state, isAutoSwipe: !state.isAutoSwipe }
    }
    case 'TOGGLE_HINTS': {
      return { ...state, testHints: action.payload }
    }
    case 'TOGGLE_FILTER': {
      return { ...state, testFilter: action.payload }
    }
    case 'TEXT_SIZE_INCREMENT': {
      return {
        ...state,
        textSizeIncrement:
          state.textSizeIncrement >= INCREMENT_LIMIT
            ? state.textSizeIncrement
            : state.textSizeIncrement + 1
      }
    }
    case 'TEXT_SIZE_DECREMENT': {
      return {
        ...state,
        textSizeIncrement:
          state.textSizeIncrement <= DECREMENT_LIMIT
            ? state.textSizeIncrement
            : state.textSizeIncrement - 1
      }
    }
    case 'DEFAULT_TEXT_SIZE': {
      return { ...state, textSizeIncrement: 0 }
    }
    case 'TOGGLE_NOTIFICATION': {
      return { ...state, isNotification: !state.isNotification }
    }
    case 'TOGGLE_REPORT': {
      return { ...state, isReportOpen: !state.isReportOpen }
    }
    default: {
      throw new Error(`Unhandled action type: ${(action as Action).type}`)
    }
  }
}

// Define the provider props interface
interface TestProviderProps {
  children: ReactNode
}

// Create the provider component
export const TestProvider: React.FC<TestProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(TestReducer, initialState)

  const value = { state, dispatch }
  return <TestContext.Provider value={value}>{children}</TestContext.Provider>
}

// Create a custom hook to use the context
export function useTest() {
  const context = useContext(TestContext)
  if (context === undefined) {
    throw new Error('useTest must be used within a TestProvider')
  }
  return context
}

// Create action dispatchers for pagination and other actions
export const toggleExplanation = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'TOGGLE_EXPLANATION' })

export const toggleModal = (
  dispatch: Dispatch<Action>,
  formType:
    | 'mark'
    | 'submit'
    | 'resume'
    | 'quick-result'
    | 'mobile-settings'
    | 'unlock'
    | null
) => dispatch({ type: 'TOGGLE_MODAL', payload: formType })

export const changeTestMode = (dispatch: Dispatch<Action>, mode: TestMode) =>
  dispatch({ type: 'CHANGE_TEST_MODE', payload: mode })

export const setTestData = (dispatch: Dispatch<Action>, testData: Quiz) =>
  dispatch({ type: 'SET_TEST_DATA', payload: testData })

export const setFilteredTestData = (
  dispatch: Dispatch<Action>,
  filteredTestData: Quiz | null
) =>
  dispatch({
    type: 'SET_FILTERED_TEST_DATA',
    payload: filteredTestData || null
  })

export const setTestExplanationData = (
  dispatch: Dispatch<Action>,
  testExplanationData: Question[]
) =>
  dispatch({ type: 'SET_TEST_EXPLANATION_DATA', payload: testExplanationData })

export const setCurrentPage = (dispatch: Dispatch<Action>, page: number) =>
  dispatch({ type: 'SET_CURRENT_PAGE', payload: page })

export const nextPage = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'NEXT_PAGE' })

export const prevPage = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'PREV_PAGE' })

export const setChatData = (dispatch: Dispatch<Action>, chatData: ChatItem[]) =>
  dispatch({ type: 'SET_CHAT_DATA', payload: chatData })

export const toggleChat = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'TOGGLE_CHAT' })

export const setTestTime = (dispatch: Dispatch<Action>, time: number) => {
  dispatch({ type: 'SET_TEST_TIME', payload: time })
}
export const toggleAutoSwipe = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'TOGGLE_AUTO_SWIPE' })

export const toggleHints = (
  dispatch: Dispatch<Action>,
  hints: 'hint-and-key-info' | 'key-info' | 'hint' | null
) => {
  dispatch({ type: 'TOGGLE_HINTS', payload: hints })
}

export const toggleFilter = (
  dispatch: Dispatch<Action>,
  filter: 'mistake' | 'correct' | null
) => {
  dispatch({ type: 'TOGGLE_FILTER', payload: filter })
}

export const setQuestionTimers = (
  dispatch: Dispatch<Action>,
  timers: number[]
) => dispatch({ type: 'SET_QUESTION_TIMERS', payload: timers })

export const incrementTextSize = (
  dispatch: Dispatch<Action>,
  increment: number
) => {
  dispatch({ type: 'TEXT_SIZE_INCREMENT', payload: increment })
}

export const decrementTextSize = (
  dispatch: Dispatch<Action>,
  decrement: number
) => {
  dispatch({ type: 'TEXT_SIZE_DECREMENT', payload: decrement })
}

export const defaultTextSize = (dispatch: Dispatch<Action>) => {
  dispatch({ type: 'DEFAULT_TEXT_SIZE' })
}

export const toggleNotification = (dispatch: Dispatch<Action>) =>
  dispatch({ type: 'TOGGLE_NOTIFICATION' })

export const toggleReport = (dispatch: Dispatch<Action>) => {
  dispatch({ type: 'TOGGLE_REPORT' })
}
